import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { BreadService } from '../bread.service';
import { BaseCrudService } from '../bread-crud.service';
import { ApiService } from '../api.service';
import { LoaderService } from '../loader.service';

export interface Invoice {
  id?: number;
  invoice_ref?: string;
  invoice_number?: number;
  invoice_type_id?: number;
  invoice_status_id?: number;
  client_id?: number;
  client?: any;
  shop_id?: number;
  shop?: any;
  order_id?: number;
  parent_id?: number;
  invoice_date?: string;
  invoice_date_limit?: string;
  net_price?: number;
  total_tax?: number;
  reduction_amount?: number;
  gross_price?: number;
  payment_status_id?: number;
  pdf_url?: string;
  uuid_public?: string;
  products?: any[];
  type?: any;
  status?: any;
  created_at?: string;
  updated_at?: string;
  custom_attributes?: any;
}

@Injectable({ providedIn: 'root' })
export class InvoiceService extends BaseCrudService<Invoice> {
  protected model = 'invoices';
  private api = inject(ApiService);
  private loader = inject(LoaderService);

  constructor(bread: BreadService) {
    super(bread);
  }

  /**
   * Send invoice by email
   */
  send(id: string | number, payload: { to?: string; subject?: string; message?: string; link?: string }): Observable<any> {
    this.loader.startLoading();
    return this.api.post<{ data: any }>(`kreosi/invoices/send-email/${id}`, payload)
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error sending invoice:', err);
          return throwError(() => err);
        }),
        finalize(() => this.loader.stopLoading())
      );
  }

  /**
   * Send reminder email for invoice
   */
  sendReminder(id: string | number): Observable<any> {
    return this.api.post<{ data: any }>(`kreosi/invoices/send-reminder-email/${id}`, {})
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error sending reminder:', err);
          throw err;
        })
      );
  }

  /**
   * Add a payment to an invoice
   */
  addPayment(id: string | number, payload: {
    total: number;
    given: number;
    change: number;
    payment_method_id: number;
    payment_date: string;
  }): Observable<any> {
    return this.api.post<{ data: any }>(`kreosi/invoices/add-payment/${id}`, payload)
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error adding invoice payment:', err);
          throw err;
        })
      );
  }

  /**
   * Get invoice by UUID (public endpoint)
   */
  getByUuid(uuid: string): Observable<Invoice> {
    return this.api.fetch<Invoice>(`kreosi/invoices/preview/${uuid}`, {
      prefix: '',
      paginate: false
    })
    .pipe(
      map(res => (res as any).data || res),
      catchError(err => {
        console.error('Error fetching invoice:', err);
        return of(null as any);
      })
    );
  }

  /**
   * Get invoice by UUID (public endpoint)
   */
  generatePdf(id: number): Observable<string> {
    return this.api.fetch<any>(`kreosi/invoices/generate-pdf/${id}`, {
      prefix: '',
      paginate: false
    })
    .pipe(
      map(res => (res as any).data || res),
      catchError(err => {
        console.error('Error generating PDF:', err);
        return of(null as any);
      })
    );
  }
}
